import { Link, useLocation, useNavigate } from 'react-router-dom'
import { BarChart3, LogOut, TreeDeciduous, User } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { useUIStore } from '@/stores/uiStore'
import { cn } from '@/utils/cn'
import { Button, ThemeToggle, Tooltip } from '@/components/common'

const Header = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { isAuthenticated, user, logout } = useAuthStore()
  const { isTreeViewOpen, setTreeViewOpen } = useUIStore()

  const isWorkspace = location.pathname.startsWith('/workspace')

  const handleLogout = () => {
    logout()
    navigate('/')
  }

  return (
    <header className="sticky top-0 z-40 w-full border-b border-slate-200/50 dark:border-slate-700/50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-xl">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link
            to={isAuthenticated ? '/projects' : '/'}
            className="flex items-center gap-3 group"
          >
            <div className="w-10 h-10 rounded-xl overflow-hidden bg-white dark:bg-slate-800 flex items-center justify-center shadow-md group-hover:shadow-lg transition-shadow">
              <img
                src="/aadya_logo.png"
                alt="AADYA logo"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="hidden sm:block">
              <h1 className="text-xl font-bold text-slate-900 dark:text-white">
                AADYA
              </h1>
              <p className="text-xs text-slate-500 dark:text-slate-400 -mt-0.5">
                Agentic AI for Designing Your problems through Abstraction
              </p>
            </div>
          </Link>

          {/* Navigation */}
          <nav className="flex items-center gap-2">
            {/* Tree view toggle (only on workspace) */}
            {isWorkspace && (
              <Tooltip content={isTreeViewOpen ? 'Hide tree' : 'Show tree'} position="bottom">
                <button
                  onClick={() => setTreeViewOpen(!isTreeViewOpen)}
                  className={cn(
                    'p-2 rounded-lg transition-colors',
                    'hover:bg-slate-100 dark:hover:bg-slate-800',
                    isTreeViewOpen && 'bg-light-icy-blue/20 dark:bg-dark-muted-teal/20 text-light-icy-blue dark:text-dark-muted-teal'
                  )}
                >
                  <TreeDeciduous className="w-5 h-5" />
                </button>
              </Tooltip>
            )}

            {/* Analytics link (only when authenticated) */}
            {isAuthenticated && (
              <Tooltip content="Pilot study analytics" position="bottom">
                <Link
                  to="/analytics"
                  className={cn(
                    'p-2 rounded-lg transition-colors',
                    'hover:bg-slate-100 dark:hover:bg-slate-800',
                    location.pathname === '/analytics' &&
                      'bg-light-icy-blue/20 dark:bg-dark-muted-teal/20 text-light-icy-blue dark:text-dark-muted-teal',
                  )}
                >
                  <BarChart3 className="w-5 h-5" />
                </Link>
              </Tooltip>
            )}

            {/* Theme toggle */}
            <ThemeToggle />

            {/* Auth section */}
            {isAuthenticated ? (
              <div className="flex items-center gap-2 ml-2 pl-2 border-l border-slate-200 dark:border-slate-700">
                <Tooltip content={`Logged in as ${user?.username}`} position="bottom">
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800">
                    <User className="w-4 h-4 text-slate-500" />
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                      {user?.username}
                    </span>
                  </div>
                </Tooltip>
                <Tooltip content="Sign out" position="bottom">
                  <button
                    onClick={handleLogout}
                    className="p-2 rounded-lg text-slate-500 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    <LogOut className="w-5 h-5" />
                  </button>
                </Tooltip>
              </div>
            ) : (
              <Link to="/login">
                <Button variant="primary" size="sm">
                  Sign In
                </Button>
              </Link>
            )}
          </nav>
        </div>
      </div>
    </header>
  )
}

export default Header
